document.addEventListener('alpine:init', () => {
  Alpine.data('PredictiveSearch', (resources) => ({
    loading: false,
    resultsOpen: false,
    rawQuery: '',
    resultsHTML: null,
    resources: resources,
    selectedElement: null,
    allOptionsArray: [],
    requestVersion: 0,
    requestController: null,
    get trimmedQuery() {
      return this.rawQuery.trim();
    },
    get queryKey() {
      return this.trimmedQuery.replace(' ', '-').toLowerCase();
    },
    init() {
      this.$watch('searchOpen', (value) => {
        if (value === true) {
          this.onOpen();

          this.$focus.focus(this.$refs.input);

          this.$nextTick(() => {
            this.$root.style.setProperty(
              '--search-bar-height',
              `${this.$root.getBoundingClientRect().height}px`
            );
          });
        }
      });

      this.$watch('trimmedQuery', (value, oldValue) => {
        if (!this.trimmedQuery.length) {
          this.closeResults();
        } else {
          this.openResults();

          if (value !== oldValue) {
            this.getSearchResults();
          }
        }
      });
    },
    close(clearSearchTerm = true, focusAfter) {
      this.closeResults(clearSearchTerm);
      this.searchOpen = false;

      if (focusAfter) {
        setTimeout(() => {
          this.$focus.focus(focusAfter);
        });
      }
    },
    openResults() {
      this.resultsOpen = true;

      document.documentElement.style.overflowY = 'hidden';
    },
    closeResults(clearSearchTerm = false) {
      this.resultsOpen = false;
      this.requestVersion += 1;
      this.requestController?.abort();
      this.loading = false;

      if (clearSearchTerm) {
        this.rawQuery = '';
      }

      this.selectedElement = null;

      document.documentElement.style.overflowY = 'auto';
    },
    async getSearchResults() {
      this.requestController?.abort();
      const version = ++this.requestVersion;
      const query = this.trimmedQuery;
      if (!query) return;
      this.requestController = new AbortController();
      this.loading = true;
      this.resultsHTML = null;
      this.selectedElement = null;
      this.allOptionsArray = [];
      liveRegion(window.theme.strings.loading);
      try {
        const response = await fetch(getURLWithParams(window.theme.routes.predictive_search_url, {
          q: query, 'resources[type]': this.resources, section_id: 'predictive-search'
        }), {signal: this.requestController.signal});
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        if (version !== this.requestVersion || query !== this.trimmedQuery || !this.searchOpen) return;
        const markup = querySelectorInHTMLString('#shopify-section-predictive-search', html);
        if (!markup) throw new Error('Search results unavailable');
        this.resultsHTML = markup.innerHTML;
        const count = querySelectorInHTMLString('#predictive-search-count', html);
        if (count) liveRegion(count.textContent);
        this.$nextTick(() => {
          this.allOptionsArray = Array.from(this.$root.querySelectorAll('[role="option"]'));
        });
        this.openResults();
      } catch (error) {
        if (error.name !== 'AbortError' && version === this.requestVersion) {
          this.resultsHTML = '<p class="p-6" role="status">Search is taking a little longer. Press Enter to see all results.</p>';
        }
      } finally {
        if (version === this.requestVersion) this.loading = false;
      }
    },
    scrollResults(event) {
      const panel = event.currentTarget;
      if (!event.deltaY || panel.scrollHeight <= panel.clientHeight) return;
      const unit = event.deltaMode === 1 ? 20 : event.deltaMode === 2 ? panel.clientHeight : 1;
      panel.scrollTop += event.deltaY * unit;
      event.preventDefault();
      event.stopPropagation();
    },
    onFocus() {
      if (!this.trimmedQuery.length) return;

      if (this.resultsHTML) {
        this.openResults();
      } else {
        this.getSearchResults();
      }
    },
    onFormSubmit(event) {
      if (
        !this.trimmedQuery.length ||
        this.$el.querySelector('[aria-selected="true"] a')
      )
        event.preventDefault();
    },
    onOpen() {
      if (this.trimmedQuery.length) {
        this.openResults();

        this.$nextTick(() => {
          this.getSearchResults();
        });
      }
    },
    switchOption(direction) {
      if (!this.allOptionsArray?.length || (direction === 'previous' && !this.selectedElement)) return;

      if (direction === 'next') {
        this.selectedElement = nextOrFirst(
          this.allOptionsArray,
          this.selectedElement
        );
      } else if (direction === 'previous') {
        this.selectedElement = previousOrLast(
          this.allOptionsArray,
          this.selectedElement
        );
      }

      this.selectedElement.scrollIntoView({
        behavior: isMotionSafe() ? 'smooth' : 'auto',
        block: 'end',
      });
    },
    onKeyup() {
      switch (this.$event.code) {
        case 'ArrowUp':
          this.switchOption('previous');
          break;
        case 'ArrowDown':
          this.switchOption('next');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    },
    onKeydown() {
      /**
       * Prevent the cursor from moving in the input when using the up and down
       * arrow keys, prevent form submission if pressing Enter on a
       * selected element
       *
       */

      if (
        this.$event.code === 'ArrowUp' ||
        this.$event.code === 'ArrowDown' ||
        (this.$event.code === 'Enter' && this.selectedElement)
      ) {
        this.$event.preventDefault();
      }
    },
    isSelected() {
      return this.$el === this.selectedElement ? 'true' : 'false';
    },
    selectOption() {
      if (this.selectedElement) {
        const selectedOptionAction =
          this.selectedElement.querySelector('a, button');

        if (selectedOptionAction) selectedOptionAction.click();
      }
    },
  }));
});
